import * as THREE from 'three';
import EventDispatcher from '../common/EventDispatcher';
import { Global } from '../common/global';
import RAPIER from '@dimforge/rapier3d-compat';
import gsap from 'gsap';
import Trail from './Trail';
import { isMobile } from 'mobile-device-detect';

const BALL_RADIUS = 0.13688752645712992 * Global.scaleFact * 1.5 * 0.55;

export default class Ball {
  constructor(scene) {
    this.scene = scene;
    this.raycaster = new THREE.Raycaster();
    this.previousPosition = new THREE.Vector3();
    this.onEvents = this.onEvents.bind(this);
  }

  init() {
    this.events = EventDispatcher.getObj();
    this.physicsWorld = Global.physicsWorld;

    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents);
    this.events.addEventListener('PLAYER_EVENTS', this.onEvents);
    this.events.addEventListener('GAME_EVENTS', this.onEvents);

    this.ballPlaced = false;
    this.canCheckForGoal = false;
    this.canCheckRaycaster = false;
    this.kickFinished = false;
    this.kickStarted = false;
    this.kickCnt = 0;
    this.velIndex = 5;
    Global.jumpHeight = 'low';
  }

  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'create_scene':
        this.addBall();
        break;
      case 'update':
        this.update(data['message']['data']);
        break;
      case 'launch_ball':
        this.launchBall(data['message']['data']);
        break;
      case 'assign_position':
        this.placeBall(data['message']['data']);
        break;
      case 'dispose':
        this.dispose();
        break;
    }
  }

  addBall() {
    // 1. Visual Ball
    this.ball = Global.assets['glbs'][`ball`]['asset'].scene.clone();
    this.scene.add(this.ball);
    this.ball.scale.set(
      Global.scaleFact * 0.5,
      Global.scaleFact * 0.5,
      Global.scaleFact * 0.5
    );
    this.ball.traverse((item) => {
      if (item.isMesh) item.castShadow = true;
    });

    // 2. Ball Shadow
    this.shadow = new THREE.Mesh(
      new THREE.PlaneGeometry(0.2, 0.1),
      new THREE.MeshBasicMaterial({
        map: Global.assets['textures'][`shadow`]['asset'],
        transparent: true,
        opacity: 0.2,
        side: THREE.DoubleSide,
      })
    );
    this.shadow.rotation.x = Math.PI / 2;
    this.scene.add(this.shadow);

    // 3. Rapier Physics Body
    let bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(0, 0, 0)
      .setLinearDamping(0.2)
      .setAngularDamping(0.4)
      .setCanSleep(false); // Kept awake for consistent trail/debug logic

    this.ballBody = this.physicsWorld.createRigidBody(bodyDesc);

    let colliderDesc = RAPIER.ColliderDesc.ball(BALL_RADIUS)
      .setRestitution(0.25)
      .setFriction(1.0)
      .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);

    this.physicsWorld.createCollider(colliderDesc, this.ballBody);

    // 4. ADDED: RED DEBUG SPHERE
    // const debugGeom = new THREE.SphereGeometry(BALL_RADIUS, 16, 16);
    // const debugMat = new THREE.MeshBasicMaterial({
    //   color: 0xff0000,
    //   wireframe: true,
    //   transparent: true,
    //   opacity: 0.8,
    // });
    // this.debugBall = new THREE.Mesh(debugGeom, debugMat);
    // this.scene.add(this.debugBall);

    // 5. Trail
    this.trail = new Trail(this.ball, {
      length: 20,
      size: 1.1 * (isMobile ? 0.75 : 1),
      decay: 0.98,
      color: 0x0000ff,
    });
    this.trail.addToScene(this.scene);

    this.ball.traverse((child) => {
      if (child.material) {
        child.material.envMap = Global.envMap;
        child.material.envMapIntensity = 1.0;
        child.material.roughness = 0.5;
      }
    });
  }

  updateTrail() {
    if (this.trail && this.trail.updateTrail) {
      this.trail.updateTrail();
    }
  }

  placeBall(data) {
    Global.canSwipe = true;
    this.trail.hideTrail();
    const { x, y, z } = data['position'];
    this.ballBody.setAngvel({ x: 0, y: 0, z: 0 }, true);
    this.ballBody.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.ballBody.setTranslation({ x, y, z }, true);
    this.kickStarted = false;
    this.ballPlaced = true;

    gsap.delayedCall(0.0, () => {
      this.events.dispatchEvent({
        type: 'PLAYER_EVENTS',
        message: {
          event_type: 'move_camera',
          data: {
            position: new THREE.Vector3(x - 0.75, y + 1.2, z),
            reset: true,
          },
        },
      });
    });
    // setTimeout(() => {
    //   this.launchBall();
    // }, 1000);
  }
  // Add this logic to your collision event handler
  handleCollision(otherBody) {
    const data = otherBody.userData;
    if (!data || !data.name) return;

    const ballPos = this.ballBody.translation();
    const s = Global.scaleFact;

    // If we hit a target segment
    if (data.name.startsWith('target_')) {
      // Check Z: Is the ball actually hitting the front/face of the bar?
      // If Z is greater than -0.15*s, it hit the front.
      if (ballPos.z > -0.15 * s) {
        console.log(`🎯 TARGET HIT: ${data.name} | Points: ${data.points}`);

        this.events.dispatchEvent({
          type: 'GAME_EVENTS',
          message: {
            event_type: 'target_hit',
            data: { points: data.points, segment: data.name },
          },
        });

        this.events.dispatchEvent({
          type: 'SOUND_EVENTS',
          message: { event_type: 'play_sound', data: { sndKey: 'ping' } },
        });
      }
    } else if (data.name === 'goal_back_net') {
      console.log('Passed into the net - No target points.');
    }
  }
  update() {
    // Step the physics world with the event queue
    // this.physicsWorld.step(eventQueue);

    // Drain the events and look for collisions

    if (this.ball && this.ballBody) {
      if (this.ball.position.z <= -5) {
        this.events.dispatchEvent({
          type: 'GAME_EVENTS',
          message: {
            event_type: 'ball_crossed_distance',
            data: {},
          },
        });
      }
      const t = this.ballBody.translation();
      const r = this.ballBody.rotation();

      // Sync Visuals
      this.ball.position.set(t.x, t.y, t.z);
      this.ball.quaternion.set(r.x, r.y, r.z, r.w);

      // Sync Debug Sphere
      if (this.debugBall) {
        this.debugBall.position.set(t.x, t.y, t.z);
      }

      // Sync Shadow
      this.shadow.position.set(t.x, 0.015 * Global.scaleFact, t.z);
      const _ss = Math.max(0, (10 - t.y * 10) * 0.1);
      this.shadow.scale.set(_ss, _ss, _ss);

      Global.ballPos = new THREE.Vector3(t.x, t.y, t.z);
      this.ball.updateMatrixWorld();

      if (this.canCheckRaycaster) this.checkForIntersections();
      if (this.kickStarted) {
        this.events.dispatchEvent({
          type: 'PLAYER_EVENTS',
          message: {
            event_type: 'move_ball',
            data: {
              position: this.ball.position.clone(),
              speed: Math.max(0.0001, this.launchVal.progress),
            },
          },
        });
        //
      }
    }
  }

  checkForIntersections() {
    const t = this.ballBody.translation();
    const currentPos = new THREE.Vector3(t.x, t.y, t.z);
    const direction = new THREE.Vector3().subVectors(
      currentPos,
      this.previousPosition
    );

    this.raycaster.ray.origin.copy(currentPos);
    this.raycaster.ray.direction.copy(direction.normalize());

    const intersectableObjects = this.scene.children.filter(
      (child) => child.userData.canCheckIntersects
    );
    const intersects = this.raycaster.intersectObjects(
      intersectableObjects,
      true
    );

    if (
      intersects.length > 0 &&
      t.z >=
        (Global.jumpHeight == 'low'
          ? -1.8
          : Global.jumpHeight == 'mid'
            ? -1.5
            : -1.4)
    ) {
      let topLevelParent = intersects[0].object;
      while (topLevelParent.parent && topLevelParent.parent !== this.scene) {
        topLevelParent = topLevelParent.parent;
      }

      if (topLevelParent.visible) {
        this.kickFinished = true;
        this.canCheckForGoal = false;
        this.canCheckRaycaster = false;
        this.events.dispatchEvent({
          type: 'SOUND_EVENTS',
          message: { event_type: 'play_sound', data: { sndKey: 'catch' } },
        });

        setTimeout(() => {
          this.events.dispatchEvent({
            type: 'SOUND_EVENTS',
            message: { event_type: 'play_sound', data: { sndKey: 'cheer' } },
          });
        }, 100);

        this.events.dispatchEvent({
          type: 'PLAYER_EVENTS',
          message: {
            event_type: 'defend',
            data: { itemType: topLevelParent.userData.itemType },
          },
        });

        const xVel = this.velIndex % 2 == 0 ? -5 : 5;
        this.ballBody.setLinvel({ x: xVel, y: 0, z: -5 }, true);
        this.ballBody.setAngvel(
          { x: -20, y: Math.random() * 10, z: Math.random() * 10 },
          true
        );
      }
    }
    this.previousPosition.copy(currentPos);
  }

  launchBall(data) {
    // console.log('launchBall', this.ballPlaced);
    if (!this.ballPlaced) return;

    this.trail.showTrail();
    Global.kicksTriggerd++;
    document.querySelector('.score_sec .try .tryLabel').innerHTML =
      `${Global.kicksTriggerd}/${Global.totalKicks}`;

    this.launchVal = {
      progress: 0.01,
    };

    this.launchTwn = gsap.to(this.launchVal, {
      progress: 0.0,
      duration: 0.5,
      delay: 0.4,
    });

    this.ballPlaced = false;
    const _velocity = Global.velocity || { x: 0, y: 6.25, z: -25 };
    // const _velocity = new THREE.Vector3(1, 6.25, -20);
    this.kickCnt++;
    const _multiplier = (Math.random() * Math.min(30, this.kickCnt)) / 1000;

    this.ballBody.setLinvel(
      {
        x: _velocity.x * (1 + _multiplier),
        y: _velocity.y,
        z: _velocity.z * (1 + _multiplier),
      },
      true
    );
    this.ballBody.setAngvel(
      {
        x: 10 + Math.random() * 5, // Forward top-spin/back-spin
        y: (Math.random() - 0.5) * 10, // Side curve
        z: (Math.random() - 0.5) * 5, // Slight wobble
      },
      true
    );
    // this.ballBody.

    this.events.dispatchEvent({
      type: 'SOUND_EVENTS',
      message: { event_type: 'play_sound', data: { sndKey: 'kick' } },
    });

    this.canCheckForGoal = true;
    this.canCheckRaycaster = true;
    this.kickStarted = true;

    setTimeout(() => {
      if (!this.kickFinished) {
        this.events.dispatchEvent({
          type: 'PLAYER_EVENTS',
          message: { event_type: 'retry', data: {} },
        });
      }
    }, 3000);
  }
  updateTrail() {
    this.trail.updateTrail();
  }
  dispose() {
    console.log('dispose');
    if (this.ball) {
      this.trail.dispose(this.scene);
      this.shadow.geometry.dispose();
      this.shadow.material.dispose();
      this.scene.remove(this.shadow);

      if (this.debugBall) {
        this.debugBall.geometry.dispose();
        this.debugBall.material.dispose();
        this.scene.remove(this.debugBall);
      }

      this.ball.traverse((child) => {
        if (child.isMesh) {
          child.geometry.dispose();
          if (child.material) {
            const mats = Array.isArray(child.material)
              ? child.material
              : [child.material];
            mats.forEach((m) => m.dispose());
          }
        }
      });
      this.scene.remove(this.ball);
    }
    if (this.physicsWorld && this.ballBody) {
      this.physicsWorld.removeRigidBody(this.ballBody);
    }
    this.events.removeEventListener('GAMESCENE_EVENTS', this.onEvents);
    this.events.removeEventListener('PLAYER_EVENTS', this.onEvents);
    this.events.removeEventListener('GAME_EVENTS', this.onEvents);
  }
}
